'use client';

import { Button } from '@shared/components/ui/button';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_ORIGIN_VALUES } from '../../constants/customerOrigins';

interface ClientsFiltersPanelProps {
  customerOrigins: string[];
  onCustomerOriginsChange: (origins: string[]) => void;
  className?: string;
}

export const ClientsFiltersPanel = ({
  customerOrigins,
  onCustomerOriginsChange,
  className,
}: ClientsFiltersPanelProps) => {
  const { t } = useTranslation('clientsModule');

  const originOptions = useMemo(
    () =>
      CUSTOMER_ORIGIN_VALUES.map((origin) => ({
        label: t(`table.customerOrigins.${origin}`, origin),
        value: origin,
      })),
    [t],
  );

  const activeFiltersCount = customerOrigins.length;

  const clearAllFilters = () => {
    onCustomerOriginsChange([]);
  };

  return (
    <div className={cn('space-y-5', className)}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('table.columns.customerOrigin', 'Origen')}>
            <MultiSelect
              options={originOptions}
              value={customerOrigins}
              onValueChange={onCustomerOriginsChange}
              placeholder={t('filters.all_origins', 'Todos los orígenes')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              searchPlaceholder={t('filters.origin_search_placeholder', 'Buscar origen...')}
              dataTestId="clients-filter-origin"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="clients-clear-filters-panel"
          >
            {t('filters.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
